import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users as UsersIcon,
  FileWarning,
  BookOpenText,
  Scale,
  ShieldCheck,
  Trash2,
  Ban,
  CircleCheck,
  Edit3,
  Plus,
  RefreshCw,
  Search,
  Eye,
  PhoneCall,
  Layers,
} from "lucide-react";
import { formatApiError } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { legalStatuses } from "@/i18n/appStrings";
import { callbackStatuses, callbackStatusLabel } from "@/i18n/helpAgentStrings";
import { LOSS_CATEGORY_KEYS } from "@/data/lossCategoryGuides";
import CaseWorkspace from "@/components/CaseWorkspace";
import SiteContentTab from "@/components/admin/SiteContentTab";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n || 0);

const Stat = ({ label, value, sub, tone = "neutral", testId }) => {
  const toneCls =
    tone === "good" ? "border-emerald-400/30" :
    tone === "warn" ? "border-amber-400/30" :
    tone === "bad" ? "border-rose-400/30" :
    "border-white/10";
  return (
    <div className={`rounded-xl border ${toneCls} bg-white/[0.03] p-4`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-mono-alt">{label}</div>
      <div className="mt-2 font-display text-2xl font-extrabold text-white">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-white/45">{sub}</div>}
    </div>
  );
};

// ---------- Overview ----------
const Overview = ({ stats, reload }) => {
  const { t } = useT();
  const a = t.admin || {};
  if (!stats) return <div className="text-white/50 py-12 text-center">{a.loading || "Loading…"}</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex justify-between items-center">
        <h3 className="font-display text-xl font-bold text-white">{a.overview || "Overview"}</h3>
        <Button variant="outline" size="sm" onClick={reload} className="border-white/15 text-white hover:bg-white/5 gap-2" data-testid="overview-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> {a.refresh || "Refresh"}
        </Button>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-mono-alt">{a.usersSection || "Users"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Stat label={a.total || "Total"} value={stats.users.total} testId="stat-users-total" />
          <Stat label={a.individuals || "Individuals"} value={stats.users.user} />
          <Stat label={a.mentors || "Mentors"} value={stats.users.mentor} tone="good" />
          <Stat label={a.advisories || "Advisories"} value={stats.users.advisory} tone="good" />
          <Stat label={a.admins || "Admins"} value={stats.users.admin} />
          <Stat label={a.blocked || "Blocked"} value={stats.users.blocked} tone={stats.users.blocked ? "bad" : "neutral"} />
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-mono-alt">{a.reportsSection || "Reports & Mentors"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <Stat label={a.reports || "Reports"} value={stats.reports.total} testId="stat-reports-total" />
          <Stat label={a.pending || "Pending"} value={stats.reports.pending} tone={stats.reports.pending ? "warn" : "neutral"} />
          <Stat label={a.published || "Published"} value={stats.reports.published} tone="good" />
          <Stat label={a.totalLoss || "Total Loss (₹)"} value={fmtINR(stats.reports.total_loss_inr)} sub={a.acrossReports} />
          <Stat label={a.mentors || "Mentors"} value={stats.mentors.total} sub={`${stats.mentors.verified} ${a.verified} · ${stats.mentors.flagged} ${a.flagged}`} />
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-mono-alt">{a.contentSection || "Content & Inbox"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label={a.reviews || "Reviews"} value={stats.reviews.total} />
          <Stat label={a.articles || "Articles"} value={stats.articles.total} />
          <Stat label={a.contactMessages || "Contact messages"} value={stats.contact_messages.total} />
          <Stat label={a.legalRequests || "Legal requests"} value={stats.legal_requests.total} sub={`${stats.legal_requests.pending ?? 0} ${a.pendingLegal || "pending"}`} />
          <Stat label={a.callbackRequests || "Callback requests"} value={stats.callback_requests?.total ?? 0} sub={`${stats.callback_requests?.pending ?? 0} ${a.pendingLegal || "pending"}`} tone={stats.callback_requests?.pending ? "warn" : "neutral"} />
        </div>
      </div>
    </div>
  );
};

// ---------- Users ----------
const UsersTab = ({ onChange, currentUserId }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(null);
  const [createForm, setCreateForm] = useState({ email: "", password: "", name: "", role: "user" });
  const qRef = useRef(q);
  qRef.current = q;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/users", {
        params: { q: qRef.current || undefined, role: role !== "all" ? role : undefined },
      });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers]);

  const onBlockToggle = async (u) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { is_blocked: !u.is_blocked });
      toast.success(u.is_blocked ? "User unblocked" : "User blocked");
      fetchUsers();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onDelete = async (u) => {
    if (u.user_id === currentUserId) {
      toast.error("You cannot delete your own account");
      return;
    }
    if (!window.confirm(`Permanently delete user ${u.email}?`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      fetchUsers();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onCreate = async () => {
    if (!createForm.email || !createForm.password) {
      toast.error("Email and password required");
      return;
    }
    try {
      await api.post("/admin/users", createForm);
      toast.success("User created");
      setCreateOpen(false);
      setCreateForm({ email: "", password: "", name: "", role: "user" });
      fetchUsers();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onSaveEdit = async () => {
    if (!editOpen) return;
    try {
      await api.patch(`/admin/users/${editOpen.user_id}`, {
        name: editOpen.name,
        email: editOpen.email,
        role: editOpen.role,
        is_blocked: editOpen.is_blocked,
      });
      toast.success("User updated");
      setEditOpen(null);
      fetchUsers();
      onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const fieldCls = "bg-white/5 border-white/15 text-white";

  return (
    <div data-testid="admin-users">
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
            placeholder="Search by email or name…"
            className="pl-9 bg-white/5 border-white/15 text-white"
            data-testid="users-search"
          />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="md:w-[180px] bg-white/5 border-white/15 text-white" data-testid="users-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="user">Individuals</SelectItem>
            <SelectItem value="mentor">Mentors</SelectItem>
            <SelectItem value="advisory">Advisories</SelectItem>
            <SelectItem value="reviewer">Reviewers</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchUsers} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="users-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
        <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold gap-2" data-testid="users-create-btn">
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
            <tr>
              <th className="px-3 py-2.5">Email</th>
              <th className="px-3 py-2.5">Name</th>
              <th className="px-3 py-2.5">Role</th>
              <th className="px-3 py-2.5">Provider</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Joined</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-white/45">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-white/45">No users found.</td></tr>
            )}
            {items.map((u) => (
              <tr key={u.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.user_id}`}>
                <td className="px-3 py-3 text-white/90">{u.email}</td>
                <td className="px-3 py-3 text-white/75">{u.name}</td>
                <td className="px-3 py-3">
                  <Badge className={
                    u.role === "admin" ? "bg-rose-500/15 text-rose-300 border-rose-500/30" :
                    u.role === "mentor" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" :
                    u.role === "advisory" ? "bg-indigo-500/15 text-indigo-300 border-indigo-500/30" :
                    "bg-white/10 text-white/75 border-white/15"
                  }>{u.role}</Badge>
                </td>
                <td className="px-3 py-3 text-white/55 text-xs">{u.provider || "email"}</td>
                <td className="px-3 py-3">
                  {u.is_blocked
                    ? <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/40">Blocked</Badge>
                    : <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Active</Badge>}
                </td>
                <td className="px-3 py-3 text-white/55 text-xs">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="border-white/15 text-white hover:bg-white/5 mr-2" onClick={() => setEditOpen({ ...u })} data-testid={`user-edit-${u.user_id}`}>
                    <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="border-white/15 text-white hover:bg-white/5 mr-2" onClick={() => onBlockToggle(u)} data-testid={`user-block-${u.user_id}`}>
                    {u.is_blocked ? <CircleCheck className="h-3.5 w-3.5 mr-1" /> : <Ban className="h-3.5 w-3.5 mr-1" />}
                    {u.is_blocked ? "Unblock" : "Block"}
                  </Button>
                  <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" onClick={() => onDelete(u)} disabled={u.user_id === currentUserId} data-testid={`user-delete-${u.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="Email" className={fieldCls} data-testid="user-create-email" />
            <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Password (min 6)" className={fieldCls} data-testid="user-create-password" />
            <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Full name" className={fieldCls} />
            <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
              <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["user", "mentor", "advisory", "reviewer", "admin"].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/15 text-white">Cancel</Button>
            <Button onClick={onCreate} className="bg-[#D4AF37] text-black font-bold" data-testid="user-create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editOpen} onOpenChange={(v) => !v && setEditOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          {editOpen && (
            <div className="space-y-3">
              <Input value={editOpen.name || ""} onChange={(e) => setEditOpen({ ...editOpen, name: e.target.value })} placeholder="Name" className={fieldCls} />
              <Input value={editOpen.email || ""} onChange={(e) => setEditOpen({ ...editOpen, email: e.target.value })} placeholder="Email" className={fieldCls} />
              <Select value={editOpen.role} onValueChange={(v) => setEditOpen({ ...editOpen, role: v })}>
                <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["user", "mentor", "advisory", "reviewer", "admin"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm text-white/80">
                <input type="checkbox" checked={!!editOpen.is_blocked} onChange={(e) => setEditOpen({ ...editOpen, is_blocked: e.target.checked })} />
                Blocked
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(null)} className="border-white/15 text-white">Cancel</Button>
            <Button onClick={onSaveEdit} className="bg-[#D4AF37] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Reports ----------
const ReportsTab = ({ onChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const qRef = useRef(q);
  qRef.current = q;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/reports", {
        params: { q: qRef.current || undefined, status: status !== "all" ? status : undefined },
      });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  useEffect(() => {
    const raw = searchParams.get("open");
    if (!raw || !raw.startsWith("scam_report:")) return;
    const id = raw.split(":")[1];
    const row = items.find((r) => r.id === id);
    if (row) setOpen(row);
  }, [searchParams, items]);

  const updateStatus = async (r, next) => {
    try {
      await api.patch(`/admin/reports/${r.id}`, { status: next, is_published: next === "published" });
      toast.success(`Marked as ${next}`);
      fetch(); onChange?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onDelete = async (r) => {
    if (!window.confirm("Delete this report permanently?")) return;
    try { await api.delete(`/admin/reports/${r.id}`); toast.success("Report deleted"); fetch(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetch()} placeholder="Search reports…" className="pl-9 bg-white/5 border-white/15 text-white" data-testid="reports-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-[200px] bg-white/5 border-white/15 text-white" data-testid="reports-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under review</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetch} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="reports-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden overflow-x-auto max-w-full">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
            <tr>
              <th className="px-3 py-2.5">Type</th>
              <th className="px-3 py-2.5">Amount</th>
              <th className="px-3 py-2.5">Platform</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Submitted</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-3 py-10 text-center text-white/45">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-white/45">No reports found.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`report-row-${r.id}`}>
                <td className="px-3 py-3 text-white/90 max-w-[200px] truncate" title={r.scam_type}>{r.scam_type}</td>
                <td className="px-3 py-3 text-white/75">₹{fmtINR(r.amount_lost)}</td>
                <td className="px-3 py-3 text-white/55 text-xs">{r.platform || "—"}</td>
                <td className="px-3 py-3"><Badge className="bg-white/10 text-white/75 border-white/15">{r.status}</Badge></td>
                <td className="px-3 py-3 text-white/55 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setOpen(r)} className="border-white/15 text-white hover:bg-white/5 mr-2" data-testid={`report-view-${r.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(r, "published")} className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 mr-2" data-testid={`report-publish-${r.id}`}>
                    Publish
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(r)} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid={`report-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-4xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Report case — {open?.scam_type}</DialogTitle>
            <DialogDescription className="text-white/55 break-all">{open?.id}</DialogDescription>
          </DialogHeader>
          {open && (
            <>
              <p className="text-sm text-white/70 line-clamp-3">{open.description}</p>
              <CaseWorkspace caseType="scam_report" caseId={open.id} onUpdated={fetch} />
            </>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-white/15 text-white hover:bg-white/5" onClick={() => { updateStatus(open, "under_review"); }}>Under review</Button>
            <Button variant="outline" className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" onClick={() => { updateStatus(open, "rejected"); }}>Reject</Button>
            <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" onClick={() => { updateStatus(open, "published"); }}>Publish</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const normalizeLegalStatus = (s) => (s === "under_review" ? "in_review" : s === "closed" ? "resolved" : s);

// ---------- Legal help requests ----------
const LegalRequestsTab = ({ onChange, isAdmin }) => {
  const { t, statusLabel } = useT();
  const a = t.admin || {};
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const qRef = useRef(q);
  qRef.current = q;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/legal-requests", {
        params: {
          q: qRef.current || undefined,
          status: status !== "all" ? status : undefined,
          category: category !== "all" ? category : undefined,
        },
      });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [status, category]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  const updateStatus = async (r, next) => {
    try {
      await api.patch(`/admin/legal-requests/${r.id}`, { status: next });
      toast.success(`${a.statusUpdated || "Status updated"}: ${statusLabel(next)}`);
      fetch();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    if (!open) return;
    try {
      await api.patch(`/admin/legal-requests/${open.id}`, { admin_notes: open.admin_notes || "" });
      toast.success(a.notesSaved || "Notes saved");
      setOpen(null);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm("Delete this legal request permanently?")) return;
    try {
      await api.delete(`/admin/legal-requests/${r.id}`);
      toast.success("Deleted");
      fetch();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-legal-requests">
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetch()}
            placeholder={a.searchLegal || "Search email, name, loss type…"}
            className="pl-9 bg-white/5 border-white/15 text-white"
            data-testid="legal-requests-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-[200px] bg-white/5 border-white/15 text-white" data-testid="legal-requests-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{a.allStatuses || "All statuses"}</SelectItem>
            {legalStatuses.map((s) => (
              <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="md:w-[180px] bg-white/5 border-white/15 text-white" data-testid="legal-requests-category-filter">
            <SelectValue placeholder={a.allCategories || "All categories"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{a.allCategories || "All categories"}</SelectItem>
            {LOSS_CATEGORY_KEYS.map((k) => (
              <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={fetch} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="legal-requests-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> {a.refresh || "Refresh"}
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
            <tr>
              <th className="px-3 py-2.5">{a.contact || "Contact"}</th>
              <th className="px-3 py-2.5">{a.lossType || "Loss type"}</th>
              <th className="px-3 py-2.5">{a.category || "Category"}</th>
              <th className="px-3 py-2.5">{a.amount || "Amount"}</th>
              <th className="px-3 py-2.5">{a.status || "Status"}</th>
              <th className="px-3 py-2.5">{a.submitted || "Submitted"}</th>
              <th className="px-3 py-2.5 text-right">{a.actions || "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-10 text-center text-white/45">{a.loading || "Loading…"}</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-white/45">{a.noLegal || "No legal help requests."}</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`legal-row-${r.id}`}>
                <td className="px-3 py-3">
                  <div className="text-white/90">{r.name || "—"}</div>
                  <div className="text-xs text-white/50">{r.email}</div>
                  {r.call_from_agent && (
                    <Badge className="mt-1 bg-[#FF3B30]/15 text-[#FF6A60] border-[#FF3B30]/30 text-[9px]">Agent contact</Badge>
                  )}
                </td>
                <td className="px-3 py-3 text-white/75 max-w-[180px] truncate" title={r.loss_type}>{r.loss_type}</td>
                <td className="px-3 py-3 text-white/55 text-xs capitalize">{r.loss_category || "—"}</td>
                <td className="px-3 py-3 text-white/75">₹{fmtINR(r.amount_lost)}</td>
                <td className="px-3 py-3">
                  <Select
                    value={normalizeLegalStatus(r.status) || "submitted"}
                    onValueChange={(v) => updateStatus(r, v)}
                  >
                    <SelectTrigger className="h-8 w-[140px] bg-white/5 border-white/15 text-white text-xs" data-testid={`legal-status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {legalStatuses.map((s) => (
                        <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 text-white/55 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setOpen({ ...r })} className="border-white/15 text-white hover:bg-white/5 mr-2" data-testid={`legal-view-${r.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> {a.view || "View"}
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(r)} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid={`legal-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{a.legalDetail || "Legal help case"} — {open?.name}</DialogTitle>
            <DialogDescription className="text-white/55">{open?.email} · {open?.id}</DialogDescription>
          </DialogHeader>
          {open && <CaseWorkspace caseType="legal_request" caseId={open.id} onUpdated={fetch} />}
          <DialogFooter>
            <Button variant="outline" className="border-white/15 text-white hover:bg-white/5" onClick={() => setOpen(null)}>{a.close || "Close"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Need help / callback requests ----------
const NeedHelpTab = ({ onChange, isAdmin }) => {
  const { t, lang } = useT();
  const a = t.admin || {};
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const qRef = useRef(q);
  qRef.current = q;

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/callback-requests", {
        params: {
          q: qRef.current || undefined,
          status: status !== "all" ? status : undefined,
        },
      });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void fetch();
  }, [fetch]);

  const updateStatus = async (r, next) => {
    try {
      await api.patch(`/admin/callback-requests/${r.id}`, { status: next });
      toast.success(`${a.statusUpdated || "Status updated"}: ${callbackStatusLabel(lang, next)}`);
      fetch();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    if (!open) return;
    try {
      await api.patch(`/admin/callback-requests/${open.id}`, { admin_notes: open.admin_notes || "" });
      toast.success(a.notesSaved || "Notes saved");
      setOpen(null);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm("Delete this callback request?")) return;
    try {
      await api.delete(`/admin/callback-requests/${r.id}`);
      toast.success("Deleted");
      fetch();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-need-help">
      <div className="flex flex-col md:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetch()}
            placeholder={a.searchCallback || "Search name, email, phone…"}
            className="pl-9 bg-white/5 border-white/15 text-white"
            data-testid="callback-requests-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-[200px] bg-white/5 border-white/15 text-white" data-testid="callback-requests-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{a.allStatuses || "All statuses"}</SelectItem>
            {callbackStatuses.map((s) => (
              <SelectItem key={s} value={s}>{callbackStatusLabel(lang, s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={fetch} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="callback-requests-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> {a.refresh || "Refresh"}
        </Button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
            <tr>
              <th className="px-3 py-2.5">{a.contact || "Contact"}</th>
              <th className="px-3 py-2.5">{a.phone || "Phone"}</th>
              <th className="px-3 py-2.5">{a.status || "Status"}</th>
              <th className="px-3 py-2.5">{a.submitted || "Submitted"}</th>
              <th className="px-3 py-2.5 text-right">{a.actions || "Actions"}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-10 text-center text-white/45">{a.loading || "Loading…"}</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-white/45">{a.noCallback || "No callback requests yet."}</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`callback-row-${r.id}`}>
                <td className="px-3 py-3">
                  <div className="text-white/90">{r.name || "—"}</div>
                  <div className="text-xs text-white/50">{r.email}</div>
                </td>
                <td className="px-3 py-3 text-white/75 font-mono-alt text-xs">{r.phone}</td>
                <td className="px-3 py-3">
                  <Select value={r.status || "new"} onValueChange={(v) => updateStatus(r, v)}>
                    <SelectTrigger className="h-8 w-[130px] bg-white/5 border-white/15 text-white text-xs" data-testid={`callback-status-${r.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {callbackStatuses.map((s) => (
                        <SelectItem key={s} value={s}>{callbackStatusLabel(lang, s)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 text-white/55 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setOpen({ ...r })} className="border-white/15 text-white hover:bg-white/5 mr-2" data-testid={`callback-view-${r.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> {a.view || "View"}
                  </Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(r)} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid={`callback-delete-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{a.callbackDetail || "Callback request"} — {open?.name}</DialogTitle>
            <DialogDescription className="text-white/55">{open?.email} · {open?.phone}</DialogDescription>
          </DialogHeader>
          {open && (
            <div className="space-y-3 text-sm">
              <div className="text-white/45 text-xs font-mono-alt break-all">{open.id}</div>
              <div>
                <Label className="text-xs text-white/55">{a.adminNotes || "Admin notes"}</Label>
                <Textarea
                  rows={4}
                  value={open.admin_notes || ""}
                  onChange={(e) => setOpen({ ...open, admin_notes: e.target.value })}
                  className="mt-1.5 bg-white/5 border-white/15 text-white resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-white/15 text-white hover:bg-white/5" onClick={() => setOpen(null)}>{a.close || "Close"}</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" onClick={saveNotes}>{a.saveNotes || "Save notes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Articles ----------
const empty = { title: "", category: "Scam Awareness", excerpt: "", body: "", read_minutes: 5, date: "" };

const ArticlesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null); // {mode: 'edit'|'new', article}

  const fetch = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/articles"); setItems(r.data || []); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const save = async (mode, a) => {
    try {
      if (mode === "new") { await api.post("/admin/articles", a); toast.success("Article published"); }
      else { await api.patch(`/admin/articles/${a.id}`, a); toast.success("Article updated"); }
      setOpen(null); fetch();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const onDelete = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"?`)) return;
    try { await api.delete(`/admin/articles/${a.id}`); toast.success("Article deleted"); fetch(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-lg font-bold text-white">Blog articles</h3>
        <Button onClick={() => setOpen({ mode: "new", a: { ...empty } })} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2" data-testid="article-new-btn">
          <Plus className="h-4 w-4" /> New article
        </Button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
            <tr>
              <th className="px-3 py-2.5">Title</th>
              <th className="px-3 py-2.5">Category</th>
              <th className="px-3 py-2.5">Date</th>
              <th className="px-3 py-2.5">Reads</th>
              <th className="px-3 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-3 py-10 text-center text-white/45">Loading…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-white/45">No articles yet.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`article-row-${a.id}`}>
                <td className="px-3 py-3 text-white/90 max-w-[400px] truncate" title={a.title}>{a.title}</td>
                <td className="px-3 py-3 text-white/65 text-xs">{a.category}</td>
                <td className="px-3 py-3 text-white/55 text-xs">{a.date}</td>
                <td className="px-3 py-3 text-white/65 text-xs">{a.read_minutes} min</td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" onClick={() => setOpen({ mode: "edit", a: { ...a } })} className="border-white/15 text-white hover:bg-white/5 mr-2" data-testid={`article-edit-${a.id}`}>
                    <Edit3 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onDelete(a)} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid={`article-delete-${a.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{open?.mode === "new" ? "New article" : "Edit article"}</DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <Input value={open.a.title} onChange={(e) => setOpen({ ...open, a: { ...open.a, title: e.target.value } })} placeholder="Title" className="bg-white/5 border-white/15 text-white" data-testid="article-input-title" />
              <div className="grid grid-cols-2 gap-3">
                <Input value={open.a.category} onChange={(e) => setOpen({ ...open, a: { ...open.a, category: e.target.value } })} placeholder="Category" className="bg-white/5 border-white/15 text-white" data-testid="article-input-category" />
                <Input value={open.a.read_minutes} onChange={(e) => setOpen({ ...open, a: { ...open.a, read_minutes: parseInt(e.target.value || 0) } })} type="number" placeholder="Read minutes" className="bg-white/5 border-white/15 text-white" />
              </div>
              <Input value={open.a.date || ""} onChange={(e) => setOpen({ ...open, a: { ...open.a, date: e.target.value } })} placeholder="Date (YYYY-MM-DD)" className="bg-white/5 border-white/15 text-white" />
              <Textarea value={open.a.excerpt} onChange={(e) => setOpen({ ...open, a: { ...open.a, excerpt: e.target.value } })} placeholder="Excerpt" rows={2} className="bg-white/5 border-white/15 text-white" data-testid="article-input-excerpt" />
              <Textarea value={open.a.body} onChange={(e) => setOpen({ ...open, a: { ...open.a, body: e.target.value } })} placeholder="Body" rows={8} className="bg-white/5 border-white/15 text-white" data-testid="article-input-body" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)} className="border-white/15 text-white hover:bg-white/5">Cancel</Button>
            <Button onClick={() => save(open.mode, open.a)} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="article-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Audit log ----------
const AuditTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/audit", { params: { limit: 150 } });
      setItems(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div data-testid="admin-audit">
      <div className="flex justify-between mb-4">
        <h3 className="font-display text-xl font-bold text-white">Audit log</h3>
        <Button onClick={load} className="bg-[#D4AF37] text-black font-bold" size="sm">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>
      <div className="rounded-xl border border-white/10 overflow-hidden max-h-[60vh] overflow-y-auto">
        {loading && <div className="p-8 text-center text-white/45">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-8 text-center text-white/45">No audit entries.</div>}
        {items.map((row) => (
          <div key={row.id} className="border-t border-white/5 px-4 py-3 text-sm hover:bg-white/[0.02]">
            <div className="flex justify-between gap-2">
              <span className="text-[#D4AF37] font-mono-alt text-xs">{row.action}</span>
              <span className="text-white/40 text-xs">{(row.created_at || "").slice(0, 19)}</span>
            </div>
            <div className="text-white/75 mt-1">{row.entity_type} · {row.entity_id}</div>
            <div className="text-white/45 text-xs mt-0.5">{row.actor_email} ({row.actor_role})</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Page ----------
const Admin = () => {
  const { t } = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const a = t.admin || {};
  const [stats, setStats] = useState(null);
  const fetchStats = async () => {
    try { const r = await api.get("/admin/stats"); setStats(r.data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { fetchStats(); }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 md:px-8 py-10" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="h-7 w-7 text-[#D4AF37]" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{a.title || "Admin Panel"}</h1>
      </div>
      <p className="text-sm text-white/55 mb-8">{a.subtitle}</p>

      <Tabs defaultValue="overview" className="w-full" data-testid="admin-tabs">
        <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="inline-flex w-max min-w-full flex-nowrap h-auto bg-white/5 border border-white/10">
          <TabsTrigger value="overview" className="shrink-0" data-testid="tab-overview"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> {a.overview || "Overview"}</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" className="shrink-0" data-testid="tab-users"><UsersIcon className="h-3.5 w-3.5 mr-1.5" /> {a.users || "Users"}</TabsTrigger>}
          <TabsTrigger value="reports" className="shrink-0" data-testid="tab-reports"><FileWarning className="h-3.5 w-3.5 mr-1.5" /> {a.scamReports || "Scam Reports"}</TabsTrigger>
          <TabsTrigger value="legal" className="shrink-0" data-testid="tab-legal"><Scale className="h-3.5 w-3.5 mr-1.5" /> {a.lossRequests || "Loss Requests"}</TabsTrigger>
          <TabsTrigger value="need-help" className="shrink-0" data-testid="tab-need-help"><PhoneCall className="h-3.5 w-3.5 mr-1.5" /> {a.needHelp || "Need Help"}</TabsTrigger>
          {isAdmin && <TabsTrigger value="articles" className="shrink-0" data-testid="tab-articles"><BookOpenText className="h-3.5 w-3.5 mr-1.5" /> {a.blog || "Blog"}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="site-content" className="shrink-0" data-testid="tab-site-content"><Layers className="h-3.5 w-3.5 mr-1.5" /> Site content</TabsTrigger>}
          <TabsTrigger value="audit" className="shrink-0" data-testid="tab-audit"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Audit</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="overview" className="mt-6"><Overview stats={stats} reload={fetchStats} /></TabsContent>
        {isAdmin && <TabsContent value="users" className="mt-6"><UsersTab onChange={fetchStats} currentUserId={user?.user_id} /></TabsContent>}
        <TabsContent value="reports" className="mt-6"><ReportsTab onChange={fetchStats} /></TabsContent>
        <TabsContent value="legal" className="mt-6"><LegalRequestsTab onChange={fetchStats} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="need-help" className="mt-6"><NeedHelpTab onChange={fetchStats} isAdmin={isAdmin} /></TabsContent>
        {isAdmin && <TabsContent value="articles" className="mt-6"><ArticlesTab /></TabsContent>}
        {isAdmin && <TabsContent value="site-content" className="mt-6"><SiteContentTab /></TabsContent>}
        <TabsContent value="audit" className="mt-6"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
